<template>
  <div class="dashboard-container">
    <TopNavBar />
    <div class="dashboard-content" :class="{
      'status-layout': menuStore.activeMenu === 'status',
      'more-stats-layout': menuStore.activeMenu === 'moreStats',
      'alarm-layout': menuStore.activeMenu === 'alarm',
      'video-layout': menuStore.activeMenu === 'video',
      'health-layout': menuStore.activeMenu === 'health',
      'culture-layout': menuStore.activeMenu === 'culture'
    }">
      <!-- 社区态势（默认） -->
      <template v-if="menuStore.activeMenu === 'status'">
        <LeftPanel />
        <CenterMap />
        <RightPanel />
      </template>
      <!-- 更多统计 -->
      <EnvironmentMonitor v-else-if="menuStore.activeMenu === 'moreStats'" />
      <!-- 视频中心 -->
      <VideoCenter v-else-if="menuStore.activeMenu === 'video'" />
      <!-- 告警中心 -->
      <AlarmCenter v-else-if="menuStore.activeMenu === 'alarm'" />
      <!-- 健康生活 -->
      <HealthLife v-else-if="menuStore.activeMenu === 'health'" />
      <!-- 社区文化 -->
      <CultureCenter v-else-if="menuStore.activeMenu === 'culture'" />
      <!-- 其他菜单暂时占位 -->
      <div v-else class="placeholder-view">
        <div class="placeholder-text">{{ getMenuName(menuStore.activeMenu) }} - 功能开发中</div>
      </div>
    </div>

    <!-- 详情弹窗 - 放在最外层，确保显示在所有元素之上 -->
    <div v-if="showDetailDialog" class="environment-detail-mask" @click="closeDetailDialog">
      <div class="environment-detail-modal" @click.stop>
        <div class="environment-detail-header">
          <div class="environment-detail-title">{{ detailDialogTitle }}</div>
          <div class="environment-detail-actions">
            <button class="icon-btn" type="button" title="关闭" @click="closeDetailDialog">×</button>
          </div>
        </div>
        <div class="environment-detail-body">
          <DetailStats
            v-if="showDetailDialog"
            :title="detailDialogData.title"
            :basic-stats="detailDialogData.basicStats"
            :chart-groups="detailDialogData.chartGroups"
            :trend-data="detailDialogData.trendData"
            :format-value="detailDialogData.formatValue"
            :get-color="detailDialogData.getColor"
            :should-hide-unit="detailDialogData.shouldHideUnit"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TopNavBar from './components/TopNavBar.vue'
import LeftPanel from './components/LeftPanel.vue'
import EnvironmentMonitor from './components/EnvironmentMonitor.vue'
import CenterMap from './components/CenterMap.vue'
import RightPanel from './components/RightPanel.vue'
import VideoCenter from './components/VideoCenter.vue'
import AlarmCenter from './components/AlarmCenter.vue'
import HealthLife from './components/HealthLife.vue'
import CultureCenter from './components/CultureCenter.vue'
import DetailStats from './components/DetailStats.vue'
import { websocketStore } from './stores/websocket'
import { menuStore } from './stores/menu'
import { communityStore } from './stores/community'
import { getCurrentCommunity } from '@/utils/vc'
import './styles/main.scss'

export default {
  name: 'BigScreen',
  components: {
    TopNavBar,
    LeftPanel,
    EnvironmentMonitor,
    CenterMap,
    RightPanel,
    VideoCenter,
    AlarmCenter,
    HealthLife,
    CultureCenter,
    DetailStats
  },
  data() {
    return {
      menuStore,
      wsStore: websocketStore,
      communityStore,
      titleCheckTimer: null, // 用于定期检查社区名称变化的定时器
      lastCommunityName: '', // 记录上次的社区名称，用于判断是否变化
      showDetailDialog: false, // 控制详情弹窗显示
      detailDialogTitle: '', // 弹窗标题
      detailDialogData: {} // 弹窗数据
    }
  },
  watch: {
    // 监听社区信息变化，动态更新浏览器标题
    'communityStore.currentCommunity'() {
      this.updateBrowserTitle()
    }
  },
  mounted() {
    // 添加全屏样式类
    document.body.classList.add('big-screen-page')
    document.getElementById('app').classList.add('big-screen-page')
    // 设置浏览器标题（立即设置一次）
    this.updateBrowserTitle()
    // 定期检查社区名称变化（每2秒检查一次，确保异步加载后也能更新）
    this.titleCheckTimer = setInterval(() => {
      this.updateBrowserTitle()
    }, 2000)
    // 监听详情弹窗事件
    this.$root.$on('show-detail-dialog', this.handleShowDetailDialog)
    // wsStore.connect('ws://localhost:8080/ws')
  },
  beforeDestroy() {
    // 清除定时器
    if (this.titleCheckTimer) {
      clearInterval(this.titleCheckTimer)
      this.titleCheckTimer = null
    }
    // 移除事件监听
    this.$root.$off('show-detail-dialog', this.handleShowDetailDialog)
    // 移除全屏样式类
    document.body.classList.remove('big-screen-page')
    const appEl = document.getElementById('app')
    if (appEl) {
      appEl.classList.remove('big-screen-page')
    }
    this.wsStore.disconnect()
  },
  methods: {
    getMenuName(menu) {
      const map = { status: '社区态势', moreStats: '更多统计', video: '视频中心', alarm: '告警中心', health: '健康生活', culture: '社区文化' }
      return map[menu] || '未知'
    },
    // 更新浏览器标题：运营驾驶舱|社区名称
    updateBrowserTitle() {
      try {
        // 从后端获取当前社区信息
        const currentCommunity = getCurrentCommunity()
        let communityName = ''

        // 尝试获取社区名称（可能字段名为 name 或 communityName）
        if (currentCommunity) {
          communityName = currentCommunity.name || currentCommunity.communityName || ''
        }

        // 如果社区名称没有变化，不需要更新标题（避免频繁更新）
        if (communityName === this.lastCommunityName && document.title) {
          return
        }

        // 记录当前社区名称
        this.lastCommunityName = communityName

        // 设置浏览器标题：运营驾驶舱|社区名称
        if (communityName) {
          document.title = `运营驾驶舱|${communityName}`
        } else {
          // 如果获取不到社区名称，只显示运营驾驶舱
          document.title = '运营驾驶舱'
        }
      } catch (e) {
        console.warn('更新浏览器标题失败:', e)
        // 如果获取失败，使用默认标题
        if (!document.title || document.title === '运营驾驶舱') {
          document.title = '运营驾驶舱'
        }
      }
    },
    handleShowDetailDialog(data) {
      this.detailDialogTitle = data.title + '详情'
      this.detailDialogData = data
      this.showDetailDialog = true
    },
    closeDetailDialog() {
      this.showDetailDialog = false
      this.detailDialogData = {}
    }
  }
}
</script>

<style lang="scss">
// 大屏全屏样式 - 不使用 scoped，确保全局生效
body.big-screen-page {
  margin: 0;
  padding: 0;
  overflow: hidden;
}

#app.big-screen-page {
  margin: 0;
  padding: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}
</style>

<style lang="scss" scoped>
.dashboard-container {
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0a1929 0%, #1a2332 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
}

.dashboard-content {
  flex: 1;
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
  &.status-layout {
    position: relative;
    padding: 0; // 移除内边距，让地图铺满整个容器
  }
  // 告警中心布局 - 移除内边距，让地图铺满整个容器
  &.alarm-layout {
    position: relative;
    padding: 0;
  }
  // 视频中心布局 - 添加内边距
  &.video-layout {
    padding: 20px;
    box-sizing: border-box;
  }
  // 健康生活布局 - 添加内边距（网格布局由组件内部控制）
  &.health-layout {
    // 移除内边距，让地图铺满整个容器
  }
  // 社区文化布局 - 添加内边距
  &.culture-layout {
    padding: 20px;
    box-sizing: border-box;
  }
  // 更多统计布局 - 添加内边距
  &.more-stats-layout {
    padding: 20px;
    box-sizing: border-box;
    // 确保 MoreStats 组件能够正确获取高度
    > * {
      height: 100%;
      min-height: 0;
      max-height: 100%; // 关键：限制最大高度
      // 注意：不要设置 overflow: hidden，让子组件自己处理滚动
    }
  }
}

.placeholder-view {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  .placeholder-text {
    font-size: 24px;
    color: #a0b3c8;
  }
}

// 详情弹窗样式 - 放在最外层，确保显示在所有元素之上
.environment-detail-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
}

.environment-detail-modal {
  position: relative;
  width: min(90%, calc(100vw - 40px));
  max-width: 1400px;
  height: min(90vh, calc(100vh - 40px));
  background: rgba(15, 30, 50, 0.92);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
}

.environment-detail-header {
  height: 44px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  background: rgba(0, 212, 255, 0.05);
}

.environment-detail-title {
  color: #00d4ff;
  font-size: 14px;
  font-weight: 700;
}

.environment-detail-actions {
  display: flex;
  gap: 6px;
}

.environment-detail-actions .icon-btn {
  width: 32px;
  height: 28px;
  border-radius: 6px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  color: #a0b3c8;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    border-color: rgba(0, 212, 255, 0.5);
    color: #00d4ff;
  }
}

.environment-detail-body {
  flex: 1;
  position: relative;
  background: rgba(15, 30, 50, 0.95);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>

